import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  IconButton,
} from "@mui/material";
import {
  Close,
  PhotoCamera,
  ListAlt,
  YouTube,
  ArrowCircleUp,
  HelpOutline,
  CheckCircle,
} from "@mui/icons-material";
import { getCalidadPI } from "../../action";

export const QualityTipsModal = ({ open, onClose, propiedad }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [calidadPI, setCalidadPI] = useState(null);

  useEffect(() => {
    if (open && propiedad && propiedad.idPublicacionPI) {
      setLoading(true);
      dispatch(getCalidadPI(propiedad.idPublicacionPI))
        .then((data) => {
          if (data && data.value && data.value.data) {
            setCalidadPI(data.value.data);
          } else {
            setCalidadPI(null);
          }
        })
        .catch((err) => {
          console.error("Error fetching quality details:", err);
          setCalidadPI(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setCalidadPI(null);
    }
  }, [open, propiedad, dispatch]);

  const getLevelLabel = (level) => {
    switch (level) {
      case "professional":
        return "Profesional";
      case "standard":
        return "Estándar";
      case "basic":
        return "Básico";
      default:
        return level || "Desconocido";
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "professional":
        return "#00a62b"; // green
      case "standard":
        return "#e6c300"; // gold/yellow
      case "basic":
        return "#c62828"; // red
      default:
        return "#757575";
    }
  };

  const getActionDetails = (actionId) => {
    switch (actionId) {
      case "picture":
        return {
          title: "Añadir más fotografías",
          description:
            "Portal Inmobiliario requiere un mínimo de fotos según el tipo de propiedad: 12 fotos para Casas, Departamentos y Oficinas; 6 para Locales y Terrenos; 4 para Estacionamientos. Las publicaciones con fotos insuficientes pierden gran exposición.",
          icon: <PhotoCamera sx={{ color: "#0288d1", fontSize: 28 }} />,
        };
      case "technical_specification":
        return {
          title: "Completar ficha técnica",
          description:
            "Asegúrate de rellenar todos los atributos obligatorios y opcionales más representativos de la propiedad (ej. año de construcción, gastos comunes, orientación). Esto permite que aparezca en las búsquedas con filtros específicos.",
          icon: <ListAlt sx={{ color: "#7b1fa2", fontSize: 28 }} />,
        };
      case "video":
        return {
          title: "Agregar video o tour virtual",
          description:
            "Enriquece la publicación añadiendo un video de YouTube o un tour virtual de Matterport. Las publicaciones con multimedia retienen mayor interés y mejoran su posicionamiento general.",
          icon: <YouTube sx={{ color: "#d32f2f", fontSize: 28 }} />,
        };
      case "upgrade_listing":
        return {
          title: "Subir categoría de publicación (Destaque)",
          description:
            "Considera destacar esta propiedad (ej. a Oro o Premium) para consumir tus cupos disponibles del plan. Esto la posicionará en las primeras páginas de búsqueda de Portal Inmobiliario.",
          icon: <ArrowCircleUp sx={{ color: "#f57c00", fontSize: 28 }} />,
        };
      default:
        return {
          title: `Optimizar parámetro: ${actionId}`,
          description:
            "Revisa los detalles de la publicación en Portal Inmobiliario para corregir este aspecto pendiente y mejorar tu puntuación.",
          icon: <HelpOutline sx={{ color: "#757575", fontSize: 28 }} />,
        };
    }
  };

  const healthPercent = calidadPI ? Math.round((calidadPI.health || 0) * 100) : 0;
  const levelColor = calidadPI ? getLevelColor(calidadPI.level) : "#757575";
  const levelLabel = calidadPI ? getLevelLabel(calidadPI.level) : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
          Consejos de Calidad: {propiedad?.codigoPropiedad}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6 }}>
            <CircularProgress size={50} sx={{ color: "#00a62b" }} />
            <Typography variant="body2" sx={{ mt: 2, color: "#666" }}>
              Consultando consejos de calidad con Portal Inmobiliario...
            </Typography>
          </Box>
        ) : calidadPI ? (
          <Box>
            {/* Cabecera del nivel y porcentaje */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2.5,
                mb: 3,
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                borderLeft: `6px solid ${levelColor}`,
              }}
            >
              {/* Círculo de porcentaje */}
              <Box sx={{ position: "relative", display: "inline-flex", mr: 3 }}>
                <CircularProgress
                  variant="determinate"
                  value={healthPercent}
                  size={65}
                  thickness={5}
                  sx={{ color: levelColor }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: "bold", fontSize: "14px" }}>
                    {`${healthPercent}%`}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#333", mb: 0.5 }}>
                  Nivel de calidad: <span style={{ color: levelColor }}>{levelLabel}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tu publicación cumple con el {healthPercent}% de los requisitos de calidad exigidos por Portal Inmobiliario.
                </Typography>
              </Box>
            </Box>

            {/* Listado de consejos */}
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#444" }}>
              Acciones pendientes para mejorar tu exposición:
            </Typography>

            {calidadPI.actions === null || calidadPI.actions === undefined ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <HelpOutline sx={{ color: "#f57c00", fontSize: 48, mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#333" }}>
                  Consejos no disponibles (403)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "350px", mt: 0.5 }}>
                  No se pudieron cargar las sugerencias de mejora. Esto ocurre si la credencial (Token) de la integración no corresponde al propietario/cuenta que publicó este ítem en Portal Inmobiliario.
                </Typography>
              </Box>
            ) : calidadPI.actions.length > 0 ? (
              <Grid container spacing={2}>
                {calidadPI.actions.map((act, index) => {
                  const details = getActionDetails(act.id);
                  return (
                    <Grid item xs={12} key={act.id || index}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "start",
                          backgroundColor: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        }}
                      >
                        <Box sx={{ mr: 2, mt: 0.2 }}>{details.icon}</Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#333", mb: 0.5 }}>
                            {details.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: "12.5px" }}>
                            {details.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <CheckCircle sx={{ color: "#00a62b", fontSize: 48, mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#333" }}>
                  ¡Publicación excelente!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "350px", mt: 0.5 }}>
                  Has alcanzado el 100% de calidad. Tu publicación cuenta con las mejores condiciones de visualización.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, textAlign: "center" }}>
            <Typography variant="subtitle2" color="error" sx={{ fontWeight: "bold" }}>
              No se pudo obtener información
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "300px", mt: 1 }}>
              Verifica si la publicación está activa en Portal Inmobiliario o si el ID del ítem ({propiedad?.idPublicacionPI}) es correcto.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ backgroundColor: "#00a62b", "&:hover": { backgroundColor: "#008b24" } }}>
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QualityTipsModal;
